import { useState } from 'react';
import { Icon } from '../components/ui/Icon';
import { Button, Chip, Dialog } from '../components/ui/primitives';
import type { SupportContact } from '../selfservice/api';
import { usePublicConfig } from '../selfservice/usePublicConfig';
import { mailtoHref, supportSummary, whatsappHref, type SupportContext } from './supportInfo';

// P-085 · Ayuda, aviso de privacidad y términos.
//
// La ayuda muestra el canal que configuró el operador (SUPPORT_CONTACT_EMAIL / SUPPORT_CONTACT_WHATSAPP) y unos «datos
// para soporte» sin nada secreto. Privacidad y términos son MARCADORES: todavía no hay textos jurídicos aprobados y aquí
// no se redacta asesoría legal. Solo se dice, con hechos del sistema, qué guarda hoy la app; el texto definitivo
// sustituirá estas secciones cuando el dueño lo apruebe.

export function SupportChannels({ support, lines }: { support: SupportContact | null; lines: string[] }) {
  const email = support?.email ?? null;
  const whatsapp = support?.whatsapp ?? null;
  if (!email && !whatsapp) {
    return <p className="n3-onb-info__lead">Escríbele a la persona que te invitó a la beta: ella te ayuda directamente.</p>;
  }
  return (
    <div className="n3-onb-info__channels">
      {whatsapp && (
        <a className="n3-onb-info__channel" href={whatsappHref(whatsapp, lines)} rel="noopener noreferrer" target="_blank">
          <Icon name="chat" size={18} /><span>Escribir por WhatsApp</span>
        </a>
      )}
      {email && (
        <a className="n3-onb-info__channel" href={mailtoHref(email, lines)}>
          <Icon name="mail" size={18} /><span>Escribir un correo</span>
        </a>
      )}
      {email && <p className="n3-onb-info__note">Correo de ayuda: {email}</p>}
    </div>
  );
}

export function HelpContent({ support, context }: { support: SupportContact | null; context: SupportContext }) {
  const lines = supportSummary(context);
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied('done');
    } catch {
      setCopied('failed');
    }
  };
  return (
    <div className="n3-onb-info__section">
      <p className="n3-onb-info__lead">Cuéntanos qué pasó o en qué paso te quedaste. Te ayudamos a terminar de preparar tu negocio o a resolver un problema.</p>
      <SupportChannels lines={lines} support={support} />
      <h3 className="n3-onb-info__subtitle">Datos para soporte</h3>
      <p className="n3-onb-info__note">Ayudan a encontrar tu negocio. No incluyen tu contraseña ni nada secreto.</p>
      <ul aria-label="Datos para soporte" className="n3-onb-info__data">
        {lines.map((line) => <li key={line}>{line}</li>)}
      </ul>
      <div className="n3-onb-info__actions">
        <Button iconStart={<Icon name="copy" size={18} />} onClick={() => void copy()} variant="secondary">Copiar datos</Button>
        {copied === 'done' && <span className="n3-onb-info__note" role="status">Copiados.</span>}
        {copied === 'failed' && <span className="n3-onb-info__note" role="status">No se pudieron copiar: puedes seleccionarlos y copiarlos.</span>}
      </div>
    </div>
  );
}

function DraftBanner({ what }: { what: string }) {
  return (
    <p className="n3-onb-info__draft" role="note">
      <Icon name="warning" size={18} />
      <span>Versión preliminar, pendiente de aprobación legal. Todavía no es {what} definitivo.</span>
    </p>
  );
}

function ContactLine({ support }: { support: SupportContact | null }) {
  if (support?.email) return <p className="n3-onb-info__note">Para dudas sobre tus datos: {support.email}.</p>;
  return <p className="n3-onb-info__note">Para dudas sobre tus datos, escríbele a la persona que te invitó a la beta.</p>;
}

export function PrivacyContent({ support }: { support: SupportContact | null }) {
  return (
    <div className="n3-onb-info__section">
      <DraftBanner what="el aviso de privacidad" />
      <p className="n3-onb-info__lead">Mientras se aprueba, esto es lo que la app guarda hoy:</p>
      <ul className="n3-onb-info__list">
        <li>Tu cuenta: tu nombre, tu correo y tu contraseña. La contraseña se guarda como una huella cifrada: nadie puede leerla.</li>
        <li>Tu negocio: lo que tú registras en la app (tiendas, productos, ventas, inventario, gastos y tu equipo).</li>
        <li>El avance de tu configuración inicial (por ejemplo, si ya creaste tu primer producto), sin tu nombre ni tu correo, para mejorar la beta.</li>
        <li>No usamos herramientas de publicidad ni de analítica de terceros.</li>
      </ul>
      <p className="n3-onb-info__lead">Tu negocio y su información no se borran solos: ni al terminar una prueba ni si dejas de usar la app. Lo que sí vence es una cuenta que nunca preparó su negocio: a más tardar a los 7 días se borran su nombre, su correo y su contraseña cifrada. Si quieres que borremos tu negocio y tus datos, pídelo por los medios de ayuda.</p>
      <ContactLine support={support} />
    </div>
  );
}

export function TermsContent({ support }: { support: SupportContact | null }) {
  return (
    <div className="n3-onb-info__section">
      <DraftBanner what="el texto de los términos de uso" />
      <ul className="n3-onb-info__list">
        <li>Durante la beta privada, usar la app no tiene costo.</li>
        <li>Es una versión en prueba: algunas funciones pueden cambiar.</li>
        <li>Los términos definitivos se publicarán aquí cuando estén aprobados.</li>
      </ul>
      <ContactLine support={support} />
    </div>
  );
}

// P-085 · Ayuda, privacidad y términos EN LA MISMA PANTALLA (un diálogo). Así se consultan desde el registro, la
// invitación o el inicio de sesión sin salir del paso en el que se está: la invitación guarda su enlace solo en memoria y
// cambiar de pantalla lo perdería (Codex R1, K2). Atrás del teléfono lo cierra, como cualquier diálogo.

export type InfoTopic = 'help' | 'privacy' | 'terms';

const TOPICS: ReadonlyArray<{ topic: InfoTopic; label: string; title: string }> = [
  { topic: 'help', label: 'Ayuda', title: '¿Necesitas ayuda?' },
  { topic: 'privacy', label: 'Privacidad', title: 'Aviso de privacidad' },
  { topic: 'terms', label: 'Términos', title: 'Términos de uso' },
];

export function InfoDialog({ initial = 'help', context, onClose }: { initial?: InfoTopic; context: SupportContext; onClose: () => void }) {
  const { config } = usePublicConfig();
  const [topic, setTopic] = useState<InfoTopic>(initial);
  const support = config?.support ?? null;
  const current = TOPICS.find((t) => t.topic === topic)!;
  return (
    <Dialog onClose={onClose} title={current.title}>
      <div className="n3-onb-info">
        <div aria-label="Temas" className="n3-onb-info__tabs" role="group">
          {TOPICS.map((t) => (
            <Chip key={t.topic} onClick={() => setTopic(t.topic)} selected={t.topic === topic}>{t.label}</Chip>
          ))}
        </div>
        {topic === 'help' && <HelpContent context={context} support={support} />}
        {topic === 'privacy' && <PrivacyContent support={support} />}
        {topic === 'terms' && <TermsContent support={support} />}
      </div>
    </Dialog>
  );
}
